import {
  CUSTOMISATION_CATEGORIES,
  SETTINGS_SCOPE,
} from "@tfg/types/constants/communication-studio.constant"
import {
  CONTENT_FILTERING_FIELDS,
  ProductAttribute,
} from "@tfg/types/constants/configurations.constants"
import { FormFileType } from "@tfg/types/schemas/file"

export enum ConfigurationsScopeType {
  CATEGORISED = "categorised",
  FLAT = "flat",
}

export type ActivitiesCategory = {
  showActivityIcons: boolean
  showCountryFlags: boolean
  showCertificates: boolean
  activitiesDepth: number
}
export type FooterCategory = {
  showLastUpdateTimestamp: boolean
  showPoweredByTFG: boolean
}
export type ComponentCustomisationCategories = {
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]: ActivitiesCategory
  [CUSTOMISATION_CATEGORIES.FOOTER]: FooterCategory
}
export type ConfigurationsScopes = [
  {
    name: SETTINGS_SCOPE.COMPONENT_CUSTOMISATION
    type: ConfigurationsScopeType.CATEGORISED
    categories: {
      [CUSTOMISATION_CATEGORIES.ACTIVITIES]: {
        showActivityIcons: boolean
        showCountryFlags: boolean
        showCertificates: boolean
        activitiesDepth: number
      }
      [CUSTOMISATION_CATEGORIES.FOOTER]: {
        showLastUpdateTimestamp: boolean
        showPoweredByTFG: boolean
      }
    }
  },
  // TODO: Placeholder here as an example waiting for business logic of publishing to be defined
  {
    name: SETTINGS_SCOPE.CONTENT_FILTERING
    type: ConfigurationsScopeType.FLAT
    props: ContentFilteringProps
  },
]

export type ComponentCustomisationConfigurationsCategories = Extract<
  ConfigurationsScopes[number],
  { name: typeof SETTINGS_SCOPE.COMPONENT_CUSTOMISATION }
>["categories"]

export const CONFIGURATIONS_SETTINGS_SCOPE = [
  SETTINGS_SCOPE.COMPONENT_CUSTOMISATION,
  SETTINGS_SCOPE.CONTENT_FILTERING,
] as const
export type ScopedConfigurationsResponse = {
  [SETTINGS_SCOPE.COMPONENT_CUSTOMISATION]: ComponentCustomisationCategories
  [SETTINGS_SCOPE.CONTENT_FILTERING]: ContentFilteringProps
}

export type PartialScopedConfigurationsResponse = {
  [SETTINGS_SCOPE.COMPONENT_CUSTOMISATION]: Partial<ComponentCustomisationCategories>
  [SETTINGS_SCOPE.CONTENT_FILTERING]?: Partial<ContentFilteringProps>
}

export type ConfigurationsSettingsScope =
  (typeof CONFIGURATIONS_SETTINGS_SCOPE)[number]

type PartialConfigurationScope =
  ConfigurationsScopes[number] extends infer Scope
    ? Scope extends {
        type: ConfigurationsScopeType.CATEGORISED
        name: typeof SETTINGS_SCOPE.COMPONENT_CUSTOMISATION
        categories: unknown
      }
      ? {
          name: Scope["name"]
          type: Scope["type"]
          categories?: {
            [K in keyof Scope["categories"]]?: Partial<Scope["categories"][K]>
          }
        }
      : Scope extends {
            type: ConfigurationsScopeType.FLAT
            name: typeof SETTINGS_SCOPE.CONTENT_FILTERING
            props: unknown
          }
        ? {
            name: Scope["name"]
            type: Scope["type"]
            props?: Partial<Scope["props"]>
          }
        : never
    : never
export type PartialConfigurationScopes = PartialConfigurationScope[]

type PartialCategories = {
  [K in keyof ConfigurationsScopes[0]["categories"]]?: Partial<
    ConfigurationsScopes[0]["categories"][K]
  >
}

export type PartialComponentCustomisationScope = {
  name: SETTINGS_SCOPE.COMPONENT_CUSTOMISATION
  type: ConfigurationsScopeType.CATEGORISED
  categories: PartialCategories
}
export type PartialConfigurations = {
  scopes: PartialConfigurationScopes
}

type CategorisedConfiguration<
  T extends Record<string, Record<string, unknown>>,
> = {
  name: typeof SETTINGS_SCOPE.COMPONENT_CUSTOMISATION
  type: ConfigurationsScopeType.CATEGORISED
  categories: T
}

type FlatConfiguration<T extends Record<string, unknown>> = {
  name: typeof SETTINGS_SCOPE.CONTENT_FILTERING
  type: ConfigurationsScopeType.FLAT
  props: T
}

export type ComponentCustomisationConfiguration = CategorisedConfiguration<
  Record<
    | typeof CUSTOMISATION_CATEGORIES.ACTIVITIES
    | typeof CUSTOMISATION_CATEGORIES.FOOTER,
    Record<string, unknown>
  >
>

export const ECOSCORE_DISPLAY_MODES = ["b2c", "b2b"] as const
export const DEFAULT_ECOSCORE_DISPLAY_MODE = ECOSCORE_DISPLAY_MODES[0]
export type EcoScoreDisplayMode = (typeof ECOSCORE_DISPLAY_MODES)[number]

export type ContentFilteringProps = {
  [CONTENT_FILTERING_FIELDS.showTraceabilityDetails]: boolean
  [CONTENT_FILTERING_FIELDS.standardsToBeShown]: number[]
  [CONTENT_FILTERING_FIELDS.logoFile]: FormFileType | null
  [CONTENT_FILTERING_FIELDS.primaryProductAttributes]: ProductAttribute[]
  [CONTENT_FILTERING_FIELDS.secondaryProductAttributes]: ProductAttribute[]
  [CONTENT_FILTERING_FIELDS.ecoScoreDataSource]: string | null
  [CONTENT_FILTERING_FIELDS.ecoScoreDisplayMode]: EcoScoreDisplayMode | null
}
export type ContentFilteringConfiguration =
  FlatConfiguration<ContentFilteringProps>
